<template>
  <div class="container-fluid m-0 p-0">
    <div class="row text-left bg-light border-bottom py-3 d-flex justify-content-between">
      <div class="col-auto">
        <h5 class="mb-0 font-weight-bold">Generate IMO Crew History List</h5>
      </div>
      <div class="col-auto">
        <button class="btn btn-xs btn-outline-secondary px-2 mr-1" title="Go to Previous Page"
                @click="goBack"
        >
          <span class="fas fa-backward"></span>
          Go Back
        </button>
      </div>

      <div class="col col-12 input-group form-inline justify-content-center mt-2">
        <label for="date-from" class="form-control-xs font-weight-bold mr-1">
          From:
        </label>
        <input type="date" id="date-from" class="form-control form-control-xs"
               v-model="params.dateFrom"
        >

        <label for="date-to" class="form-control-xs font-weight-bold ml-3 mr-1">
          To:
        </label>
        <input type="date" id="date-to" class="form-control form-control-xs"
               v-model="params.dateTo"
        >

        <label for="vessels" class="form-control-xs font-weight-bold ml-3 mr-1" >Select Vessel: </label>
        <select class="form-control form-control-xs" id="vessels" v-model="params.vesselId">
          <option :value="undefined">---Select Vessel---</option>
          <option v-for="vessel in vesselKeys" :key="vessel.id" :value="vessel.id">
            {{vessel.name}}
          </option>
        </select>

        <label for="rank" class="form-control-xs font-weight-bold ml-3 mr-1" >Select Rank: </label>
        <select class="form-control form-control-xs" id="rank" v-model="params.rankId">
          <option :value="undefined">---Rank---</option>
          <option v-for="rank in rankKeys" :key="rank.id" :value="rank.id" :title="rank.name">
            {{rank.alias}}
          </option>
        </select>

        <label for="manning" class="form-control-xs font-weight-bold ml-3 mr-1" >Select Manning: </label>
        <select class="form-control form-control-xs" id="manning" v-model="params.manningId">
          <option :value="undefined">---Select Manning---</option>
          <option v-for="manning in getManningLists" :key="manning.id" :value="manning.id">
            {{manning.alias}}
          </option>
        </select>

        <button class="btn btn-sm btn-xs btn-secondary ml-2" @click="updateImoCrewHistory">
          <span class="fas fa-search"></span>
        </button>

        <button class="btn btn-sm btn-outline-primary font-weight-bolder ml-2" @click="downloadImoCrewHistoryFile">
          <span class="fas fa-file-pdf text-danger"></span>
        </button>
      </div>

      <table class="table table-hover table-bordered table-template-1 m-2 mb-0 text-small col col-12 sticky-header">
        <thead>
        <tr>
          <th>No</th>
          <th>Family name, given names</th>
          <th>Rank or rating</th>
          <th>Nationality</th>
          <th>Date and place of birth</th>
          <th rowspan="2">
            <p class="header-title tx-center mb-0">Passport Number and <br />Expiry Date</p>
          </th>

          <th rowspan="2">
            <p class="header-title tx-center mb-0">Seaman's Book and <br /> Expiry Date</p>
          </th>
          <th rowspan="2">
            <p class="header-title tx-center mb-0">Embarkation Date <br/> and Place</p>
          </th>
        </tr>
        </thead>

        <tbody class="text-small">
        <tr v-if="loadingReport">
          <td colspan="100%">
            <app-loading></app-loading>
          </td>
        </tr>

        <template v-else>
          <tr v-for="(crew, index) in imoCrewHistory" :key="crew.id">
            <td>{{ index + 1 }}</td>
            <td :title="isSystemAdmin ? crew.id : ''">
              <router-link :to="{name:'CrewActivity',params:{id:crew.id}}" target="_blank"
                           v-if="crew.fullName" class="mb-0">{{ crew.fullName }}</router-link>
              <p class="text-danger font-weight-bolder mb-0" v-else>NO DATA</p>
            </td>
            <td>
              <p v-if="crew.rank" class="mb-0">
               {{crew.rank}}
              </p>
              <p class="text-danger font-weight-bolder mb-0" v-else>NO DATA</p>
            </td>
            <td>
              <p v-if="crew.nationality" class="mb-0">{{ crew.nationality }}</p>
              <p class="text-danger font-weight-bolder mb-0" v-else>NO DATA</p>
            </td>
            <td class="text-center">
              <p v-if="crew.birthDate" class="mb-0">{{ crew.birthDate }}</p>
              <p class="text-danger font-weight-bolder mb-0" v-else>NO DATA</p>

              <p v-if="crew.birthPlace" class="mb-0">{{ crew.birthPlace }}</p>
              <p class="text-danger font-weight-bolder mb-0" v-else>NO DATA</p>
            </td>
            <td class="text-center">
              <p class="mb-0 font-weight-bold">PASSPORT:</p>
              <p v-if="crew.passportNumber" class="mb-0">{{ crew.passportNumber }}</p>
              <p v-else class="text-danger font-weight-bolder mb-0">NO DATA</p>

              <p v-if="crew.passportExpiryDate" class="mb-0">{{ crew.passportExpiryDate }}</p>
              <p v-else class="text-danger font-weight-bolder mb-0">NO DATA</p>
            </td>
            <td class="text-center">
              <p class="mb-0 font-weight-bold">SEAMANS BOOK:</p>
              <p v-if="crew.seamansBookNumber" class="mb-0">{{ crew.seamansBookNumber }}</p>
              <p v-else class="text-danger font-weight-bolder mb-0">NO DATA</p>

              <p v-if="crew.seamansBookExpiryDate" class="mb-0">{{ crew.seamansBookExpiryDate }}</p>
              <p v-else class="text-danger font-weight-bolder mb-0">NO DATA</p>
            </td>
            <td class="text-center">
              <p v-if="crew.embarkationDate" class="mb-0">
                {{
                  crew.previousCrewActivity.hasOwnProperty('start_date') ?
                  crew.previousCrewActivity.start_date : crew.embarkationDate
                }}
              </p>
              <p v-else class="text-danger font-weight-bolder mb-0">NO DATA</p>

              <p v-if="crew.embarkationPlace" class="mb-0">
                {{
                  crew.previousCrewActivity.hasOwnProperty('start_port') ?
                      crew.previousCrewActivity.start_port : crew.embarkationPlace
                }}
              </p>
              <p v-else class="text-danger font-weight-bolder mb-0">NO DATA</p>
            </td>
          </tr>
        </template>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters, mapActions } from 'vuex';
import { vueAppMixin } from "@/mixins/vueAppMixin";
import { dataMixin } from "@/mixins/dataMixin";
import { gateMixin } from "@/mixins/gateMixin";
import { DateService } from "@/services/Utilities/DateService";

import { ReportService } from "@/services/ReportService";

import AppLoading from "@/components/elements/AppLoading";

export default {
  name: "IMOHistory",


  mixins: [ vueAppMixin, dataMixin, gateMixin ],

  components: { AppLoading },

  data() {
    return {
      params: {
        dateFrom: DateService.dateToday(),
        dateTo: DateService.dateToday(),
      },
      loadingReport: false,
    }
  },

  methods: {
    ...mapActions([
      'getVesselKeys',
      'getRankKeys',
      'getManningKeys',
      'setLoading',
      'getImoCrewHistory',
    ]),

    async updateImoCrewHistory() {
      this.loadingReport = true;
      await this.getImoCrewHistory(this.params);
      this.loadingReport = false;
    },

    async downloadImoCrewHistoryFile() {
      await ReportService.generateImoCrewHistoryFile(this.params);
    }

  },

  computed: {
    ...mapGetters([
      'vesselKeys',
        'rankKeys',
        'manningKeys',
        'imoCrewHistory',
    ]),

    getManningLists(){
      return this.manningKeys.filter((manning)=>{
        return this.isKline ? ![7,17].includes(manning.id) : true
      })
    }
  },

  created() {
    this.getVesselKeys();
    this.getRankKeys();
    this.getManningKeys();
    this.setLoading(false);
    this.searchImoCrewHistory = _.debounce(this.updateImoCrewHistory, 500);
  },

  watch: {
    'params.vesselId'() {
      this.updateImoCrewHistory();
    },
    'params.rankId'() {
      this.updateImoCrewHistory();
    },
    'params.manningId'() {
      this.updateImoCrewHistory();
      if (this.isOchg && this.isSystemAdmin) {
        this.getVesselKeys({manningId:this.params.manningId})
      }
    },
    'params.dateFrom'() {
      if(Date.parse(this.params.dateFrom) && this.params.dateFrom > true) {
        this.searchImoCrewHistory();
      }
    },
    'params.dateTo'() {
      if(Date.parse(this.params.dateTo) && this.params.dateTo > true) {
        this.searchImoCrewHistory();
      }
    }
  }

}
</script>

<style scoped>

</style>
